import {
  ArrowUp,
  Bold,
  Italic,
  Link2,
  List,
  ListOrdered,
  Paperclip,
  Square,
  Strikethrough,
  Underline,
  X,
} from 'lucide-react'
import React, { useCallback, useRef, useEffect, useState, useMemo } from 'react'
import { useEditor, EditorContent, type Editor } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import Placeholder from '@tiptap/extension-placeholder'
import TiptapUnderline from '@tiptap/extension-underline'
import { Column, Row } from 'src/shared/components/Semantic/all'
import { cn } from 'src/shared/components/ui/utils'
import { useSupabaseClient } from 'src/api/supabase'
import { useToast } from 'src/shared/hooks/use-toast'
import { useLocalization } from 'src/shared/hooks/useLocalization'
import { AnimatedSuggestionPlaceholder } from './AnimatedSuggestionPlaceholder'
import { SaraMentionHighlight } from './SaraMentionHighlight'
import { SARA_PREFILL_TEXT, isSaraPrefillOnly } from './saraPrefill'

export type ChatInputTabSuggestion = {
  /** Shown in the empty-state hint, e.g. "Add to order and create mockups". */
  hintLabel: string
  /** Inserted after the Sara mention when Tab is pressed. */
  insertText: string
}

const ATTACHMENTS_BUCKET = 'order-message-attachments'
const MAX_FILE_SIZE_MB = 5
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024
const CHAT_INPUT_LINE_HEIGHT_PX = 22
const CHAT_INPUT_MAX_HEIGHT_PX = 144
const IMAGE_PATH_EXT_RE = /\.(png|jpe?g|gif|webp|svg|avif|bmp)(\?|#|$)/i

const sanitizeFilename = (name: string): string => {
  return name.replace(/[^a-zA-Z0-9.-]/g, '_').slice(0, 100)
}

const extFromImageMime = (mime: string): string => {
  if (mime === 'image/svg+xml') return 'svg'
  if (mime === 'image/jpeg') return 'jpg'
  const subtype = mime.split('/')[1]?.split('+')[0]
  return subtype || 'png'
}

const pastedImageName = (ext: string, index: number): string =>
  `pasted-image${index > 0 ? `-${index + 1}` : ''}.${ext}`

const looksLikeImageUrl = (url: string): boolean => {
  if (url.startsWith('data:image/')) return true
  try {
    return IMAGE_PATH_EXT_RE.test(new URL(url).pathname)
  } catch {
    return IMAGE_PATH_EXT_RE.test(url)
  }
}

const fileFromDataUri = (dataUri: string, index: number): File | null => {
  const match = dataUri.match(
    /^data:(image\/[a-zA-Z0-9.+-]+)((?:;[^,]*)*),([\s\S]+)$/
  )
  if (!match) return null

  const mime = match[1]
  const params = match[2] ?? ''
  const payload = match[3]
  const name = pastedImageName(extFromImageMime(mime), index)

  try {
    if (/;base64/i.test(params)) {
      const binary = atob(payload)
      const bytes = new Uint8Array(binary.length)
      for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i)
      return new File([bytes], name, { type: mime })
    }

    let decoded = payload
    try {
      decoded = decodeURIComponent(payload)
    } catch {
      // Unencoded SVG / malformed % sequences — use the raw payload.
    }
    return new File([decoded], name, { type: mime })
  } catch {
    return null
  }
}

const fileFromSvgMarkup = (svg: string, index: number): File =>
  new File([svg], pastedImageName('svg', index), { type: 'image/svg+xml' })

const clipboardImageFiles = (clipboard: DataTransfer): File[] => {
  const fromFiles = Array.from(clipboard.files ?? []).filter((file) =>
    file.type.startsWith('image/')
  )
  if (fromFiles.length) return fromFiles

  const fromItems: File[] = []
  for (const item of Array.from(clipboard.items ?? [])) {
    if (item.kind !== 'file' || !item.type.startsWith('image/')) continue
    const file = item.getAsFile()
    if (file) fromItems.push(file)
  }
  return fromItems
}

const imageSourcesFromHtml = (html: string): string[] => {
  if (!html) return []
  const doc = new DOMParser().parseFromString(html, 'text/html')
  return [...doc.querySelectorAll('img')]
    .map((img) => img.getAttribute('src')?.trim() ?? '')
    .filter(
      (src) => src.startsWith('data:image/') || src.startsWith('http')
    )
}

const parsePastedImageSources = (
  text: string,
  html: string
): { files: File[]; remoteUrls: string[]; leftover: string } => {
  const files: File[] = []
  const remoteUrls: string[] = []
  const seen = new Set<string>()

  const addDataOrSvg = (source: string) => {
    const value = source.trim()
    if (!value || seen.has(value)) return
    const file = value.startsWith('<svg')
      ? fileFromSvgMarkup(value, files.length)
      : fileFromDataUri(value, files.length)
    if (!file) return
    seen.add(value)
    files.push(file)
  }

  const addRemote = (url: string) => {
    if (!url || seen.has(url)) return
    seen.add(url)
    remoteUrls.push(url)
  }

  const trimmed = text.trim()
  if (trimmed.startsWith('data:image/')) {
    addDataOrSvg(trimmed)
    return { files, remoteUrls, leftover: '' }
  }
  if (/^<svg[\s\S]*<\/svg>$/i.test(trimmed)) {
    addDataOrSvg(trimmed)
    return { files, remoteUrls, leftover: '' }
  }

  let leftover = text.replace(
    /data:image\/[a-zA-Z0-9.+-]+(?:;[^,\s]*)?,\S+/g,
    (uri) => {
      addDataOrSvg(uri)
      return ' '
    }
  )

  leftover = leftover.replace(/https?:\/\/[^\s<>"']+/g, (url) => {
    const cleaned = url.replace(/[),.;!?]+$/, '')
    if (!looksLikeImageUrl(cleaned)) return url
    addRemote(cleaned)
    return ' '
  })

  for (const src of imageSourcesFromHtml(html)) {
    if (src.startsWith('data:image/')) addDataOrSvg(src)
    else addRemote(src)
  }

  for (const url of remoteUrls) {
    leftover = leftover.split(url).join(' ')
  }

  return {
    files,
    remoteUrls,
    leftover: leftover.replace(/[ \t]{2,}/g, ' ').trim(),
  }
}

const mimeFromImageUrl = (url: string): string => {
  try {
    const path = new URL(url).pathname.toLowerCase()
    if (path.endsWith('.svg')) return 'image/svg+xml'
    if (path.endsWith('.jpg') || path.endsWith('.jpeg')) return 'image/jpeg'
    if (path.endsWith('.webp')) return 'image/webp'
    if (path.endsWith('.gif')) return 'image/gif'
    if (path.endsWith('.avif')) return 'image/avif'
    if (path.endsWith('.bmp')) return 'image/bmp'
  } catch {
    // Not a valid URL — fall through to png.
  }
  return 'image/png'
}

const fileFromImageUrl = async (
  url: string,
  index: number
): Promise<File | null> => {
  try {
    const response = await fetch(url)
    if (!response.ok) return null
    const blob = await response.blob()
    const mime = blob.type.startsWith('image/')
      ? blob.type
      : looksLikeImageUrl(url)
        ? mimeFromImageUrl(url)
        : ''
    if (!mime.startsWith('image/')) return null
    return new File([blob], pastedImageName(extFromImageMime(mime), index), {
      type: mime,
    })
  } catch {
    return null
  }
}

const ToolbarDivider = () => (
  <div className="mx-0.5 h-4 w-px shrink-0 bg-gray-200" aria-hidden />
)

const SaraTabHintPlaceholder = ({
  toWriteTo,
  actionLabel,
}: {
  toWriteTo: string
  actionLabel?: string
}) => (
  <span
    className="pointer-events-none absolute left-5 right-4 top-3.5 z-[2] flex flex-wrap items-center gap-x-1.5 gap-y-1 text-[15px] leading-[1.46667] text-muted-foreground/55"
    aria-hidden
  >
    <kbd className="hidden h-[18px] min-w-[28px] items-center justify-center rounded border border-gray-200 bg-gray-50 px-1.5 font-sans text-[11px] font-medium uppercase tracking-wide text-muted-foreground/70 shadow-[0_1px_0_rgba(0,0,0,0.04)] md:inline-flex">
      tab
    </kbd>
    {actionLabel ? (
      <span>{actionLabel}</span>
    ) : (
      <>
        <span>{toWriteTo}</span>
        <span className="mr-1.5 inline-block rounded-md bg-violet-50/70 px-1.5 py-1 align-middle text-[13px] font-semibold leading-none text-violet-700/45 shadow-[inset_0_0_0_1px_rgba(124,58,237,0.1)]">
          Sara
        </span>
      </>
    )}
  </span>
)

const FormatButton = ({
  onClick,
  isActive,
  disabled,
  ariaLabel,
  children,
}: {
  onClick: () => void
  isActive?: boolean
  disabled?: boolean
  ariaLabel: string
  children: React.ReactNode
}) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    aria-label={ariaLabel}
    className={cn(
      'rounded-md p-1 text-muted-foreground transition-colors',
      isActive && 'bg-gray-100 text-foreground',
      !isActive && 'hover:bg-gray-50 hover:text-foreground',
      disabled && 'cursor-not-allowed opacity-40'
    )}
  >
    {children}
  </button>
)

const AttachmentChip = ({
  file,
  onRemove,
  removeLabel,
}: {
  file: File
  onRemove: () => void
  removeLabel: string
}) => {
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)

  useEffect(() => {
    if (!file.type.startsWith('image/')) return
    const url = URL.createObjectURL(file)
    setPreviewUrl(url)
    return () => URL.revokeObjectURL(url)
  }, [file])

  return (
    <Row className="items-center gap-1.5 rounded-lg bg-gray-50 px-2.5 py-1.5 text-xs text-gray-700">
      {previewUrl ? (
        <img
          src={previewUrl}
          alt=""
          className="h-7 w-7 rounded object-contain"
        />
      ) : null}
      <span className="max-w-[120px] truncate">{file.name}</span>
      <button
        type="button"
        onClick={onRemove}
        aria-label={removeLabel}
        className="rounded-md p-0.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </Row>
  )
}

export const ChatInputForm = ({
  conversationId,
  uploadTargetId,
  uploadBucket = ATTACHMENTS_BUCKET,
  onSubmit,
  disabled,
  isPending,
  initialContent = '',
  onDraftChange,
  channelName,
  className,
  embedded,
  noteMode,
  placeholderOverride,
  highlightSaraMentions = false,
  prefillSaraMention = false,
  tabSuggestion,
  isGenerating = false,
  onStop,
}: {
  conversationId: number | undefined | null
  /** Fallback storage folder id when conversationId is missing (e.g. Sara chat id). */
  uploadTargetId?: number | null
  uploadBucket?: string
  onSubmit: (
    content: string,
    attachmentUrls?: string[],
    plainText?: string
  ) => Promise<void>
  disabled: boolean
  isPending: boolean
  /** Sara is answering — keep the composer editable and show Stop. */
  isGenerating?: boolean
  onStop?: () => void
  initialContent?: string
  onDraftChange?: (html: string) => void
  channelName?: string
  className?: string
  embedded?: boolean
  /** When true, messages post to the thread but do not notify the team yet. */
  noteMode?: boolean
  placeholderOverride?: string
  /** Highlights @Sara / sara tokens in the editor (Request chat agent routing). */
  highlightSaraMentions?: boolean
  /** Keep the Sara mention badge in the composer (admin thread). */
  prefillSaraMention?: boolean
  /** Sara quick-action suggestion — Tab inserts Sara + phrase. */
  tabSuggestion?: ChatInputTabSuggestion | null
}) => {
  const { t } = useLocalization()
  const supabase = useSupabaseClient()
  const { toast } = useToast()
  const handleSubmitRef = useRef<() => void>(() => {})
  const handleTabRef = useRef<(event: KeyboardEvent) => boolean>(() => false)
  const handlePasteRef = useRef<(event: ClipboardEvent) => boolean>(() => false)
  const onDraftChangeRef = useRef(onDraftChange)
  onDraftChangeRef.current = onDraftChange
  const fileInputRef = useRef<HTMLInputElement>(null)
  const editorAreaRef = useRef<HTMLDivElement>(null)
  const measureSuggestionOffsetRef = useRef<() => void>(() => {})
  const [isEmpty, setIsEmpty] = useState(true)
  const [editorPlainText, setEditorPlainText] = useState('')
  const [suggestionOffset, setSuggestionOffset] = useState({ left: 0, top: 0 })
  const [attachments, setAttachments] = useState<File[]>([])
  const [isDragOver, setIsDragOver] = useState(false)

  const storageFolderId = uploadTargetId ?? conversationId
  const showSaraTabHint = highlightSaraMentions && isEmpty && !placeholderOverride
  const showTabSuggestionGhost =
    highlightSaraMentions &&
    tabSuggestion &&
    isSaraPrefillOnly(editorPlainText) &&
    !placeholderOverride

  const saraComposerAriaLabel = tabSuggestion
    ? t(
        `Tab to ${tabSuggestion.hintLabel}`,
        `Tab för att ${tabSuggestion.hintLabel.toLowerCase()}`
      )
    : t(
        'Tab to ask Sara',
        'Tab för att fråga Sara'
      )

  const placeholder = showSaraTabHint
    ? ''
    : placeholderOverride ??
      (noteMode
        ? t('Write your message...', 'Skriv ditt meddelande...')
        : channelName
          ? t(`Message #${channelName}`, `Meddelande #${channelName}`)
          : t('Write a message...', 'Skriv ett meddelande...'))

  const extensions = useMemo(
    () => [
      StarterKit.configure({
        link: {
          openOnClick: false,
          HTMLAttributes: { target: '_blank', rel: 'noopener noreferrer' },
        },
      }),
      TiptapUnderline,
      Placeholder.configure({ placeholder }),
      ...(highlightSaraMentions ? [SaraMentionHighlight] : []),
    ],
    [highlightSaraMentions, placeholder]
  )

  const syncEditorHeight = useCallback(() => {
    const prose = editorAreaRef.current?.querySelector(
      '.tiptap'
    ) as HTMLElement | null
    if (!prose) return

    prose.style.height = 'auto'
    const mentionExtraPx = highlightSaraMentions ? 4 : 0
    const nextHeight = Math.min(
      Math.max(
        prose.scrollHeight + mentionExtraPx,
        CHAT_INPUT_LINE_HEIGHT_PX + mentionExtraPx
      ),
      CHAT_INPUT_MAX_HEIGHT_PX
    )
    prose.style.height = `${nextHeight}px`
    prose.style.overflowY =
      prose.scrollHeight > CHAT_INPUT_MAX_HEIGHT_PX ? 'auto' : 'visible'
  }, [highlightSaraMentions])

  const editorProps = useMemo(
    () => ({
      attributes: {
        class:
          'outline-none py-0 text-[15px] leading-[1.46667] [&_p]:m-0 [&_p]:leading-[1.46667]',
        ...(highlightSaraMentions
          ? { 'aria-label': saraComposerAriaLabel }
          : {}),
      },
      handleKeyDown: (_: unknown, event: KeyboardEvent) => {
        if (event.key === 'Enter' && !event.shiftKey) {
          event.preventDefault()
          handleSubmitRef.current()
          return true
        }
        if (event.key === 'Tab' && !event.shiftKey) {
          return handleTabRef.current(event)
        }
      },
      handlePaste: (_view: unknown, event: ClipboardEvent) => {
        return handlePasteRef.current(event)
      },
    }),
    [highlightSaraMentions, saraComposerAriaLabel]
  )

  const editor = useEditor(
    {
      extensions,
      content: initialContent || '',
      editorProps,
      editable: !disabled && !isPending,
      onUpdate: ({ editor: activeEditor }) => {
        const text = activeEditor.getText()
        setIsEmpty(activeEditor.isEmpty)
        setEditorPlainText(text)
        onDraftChangeRef.current?.(activeEditor.getHTML())

        requestAnimationFrame(() => {
          syncEditorHeight()
          measureSuggestionOffsetRef.current()
        })
      },
    },
    [conversationId, syncEditorHeight]
  )

  const insertSaraMention = useCallback(
    (activeEditor: Editor) => {
      // JSON content keeps the trailing space that HTML parsing would strip,
      // so the caret lands to the right of the Sara mention highlight.
      activeEditor
        .chain()
        .setContent({
          type: 'doc',
          content: [
            {
              type: 'paragraph',
              content: [{ type: 'text', text: SARA_PREFILL_TEXT }],
            },
          ],
        })
        .focus('end')
        .run()
      const text = activeEditor.getText()
      setEditorPlainText(text)
      setIsEmpty(activeEditor.isEmpty)
      onDraftChangeRef.current?.(activeEditor.getHTML())
      requestAnimationFrame(syncEditorHeight)
    },
    [syncEditorHeight]
  )

  const insertSaraWithSuggestion = useCallback(
    (activeEditor: Editor, insertText: string) => {
      const nextText = `${SARA_PREFILL_TEXT}${insertText} `
      activeEditor
        .chain()
        .setContent({
          type: 'doc',
          content: [
            {
              type: 'paragraph',
              content: [{ type: 'text', text: nextText }],
            },
          ],
        })
        .focus('end')
        .run()
      const text = activeEditor.getText()
      setEditorPlainText(text)
      setIsEmpty(activeEditor.isEmpty)
      onDraftChangeRef.current?.(activeEditor.getHTML())
      requestAnimationFrame(syncEditorHeight)
    },
    [syncEditorHeight]
  )

  const measureSuggestionOffset = useCallback(() => {
    if (!editorAreaRef.current) return

    const mention = editorAreaRef.current.querySelector(
      '.sara-mention-highlight'
    ) as HTMLElement | null
    if (!mention) return

    const mentionRect = mention.getBoundingClientRect()
    const areaRect = editorAreaRef.current.getBoundingClientRect()

    setSuggestionOffset({
      left: mentionRect.right - areaRect.left + 4,
      top: mentionRect.top - areaRect.top,
    })
  }, [])

  const acceptTabSuggestion = useCallback(() => {
    if (!editor || editor.isDestroyed || !tabSuggestion) return false
    insertSaraWithSuggestion(editor, tabSuggestion.insertText)
    return true
  }, [editor, insertSaraWithSuggestion, tabSuggestion])

  useEffect(() => {
    handleTabRef.current = (event) => {
      if (!highlightSaraMentions || !editor || editor.isDestroyed) return false
      if (disabled || isPending) return false

      const text = editor.getText()

      if (tabSuggestion) {
        if (!text.trim()) {
          event.preventDefault()
          insertSaraWithSuggestion(editor, tabSuggestion.insertText)
          return true
        }
        if (isSaraPrefillOnly(text)) {
          event.preventDefault()
          acceptTabSuggestion()
          return true
        }
      }

      if (text.trim() && !isSaraPrefillOnly(text)) return false

      event.preventDefault()
      if (isSaraPrefillOnly(text)) return true

      insertSaraMention(editor)
      return true
    }
  }, [
    acceptTabSuggestion,
    disabled,
    editor,
    highlightSaraMentions,
    insertSaraMention,
    insertSaraWithSuggestion,
    isPending,
    tabSuggestion,
  ])

  useEffect(() => {
    measureSuggestionOffsetRef.current = measureSuggestionOffset
  }, [measureSuggestionOffset])

  useEffect(() => {
    if (!showTabSuggestionGhost) {
      setSuggestionOffset({ left: 0, top: 0 })
      return
    }

    measureSuggestionOffset()
    let raf2 = 0
    const raf = requestAnimationFrame(() => {
      measureSuggestionOffset()
      raf2 = requestAnimationFrame(measureSuggestionOffset)
    })

    const mention = editorAreaRef.current?.querySelector(
      '.sara-mention-highlight'
    )
    const observer =
      mention && typeof ResizeObserver !== 'undefined'
        ? new ResizeObserver(measureSuggestionOffset)
        : null
    if (mention && observer) observer.observe(mention)

    return () => {
      cancelAnimationFrame(raf)
      cancelAnimationFrame(raf2)
      observer?.disconnect()
    }
  }, [editorPlainText, measureSuggestionOffset, showTabSuggestionGhost])

  useEffect(() => {
    if (!prefillSaraMention || !editor || editor.isDestroyed) return
    const text = editor.getText()
    if (text.trim() && !isSaraPrefillOnly(text)) return
    if (isSaraPrefillOnly(text)) return
    insertSaraMention(editor)
  }, [editor, insertSaraMention, prefillSaraMention])

  useEffect(() => {
    if (!editor || editor.isDestroyed) return
    editor.setEditable(!disabled && !isPending)
  }, [editor, disabled, isPending])

  useEffect(() => {
    if (!editor || editor.isDestroyed) return

    const handleResize = () => {
      syncEditorHeight()
    }

    handleResize()
    editor.on('update', handleResize)

    return () => {
      editor.off('update', handleResize)
    }
  }, [editor, syncEditorHeight])

  const addAttachmentFiles = useCallback(
    (files: File[]) => {
      const valid: File[] = []
      for (const file of files) {
        if (file.size > MAX_FILE_SIZE_BYTES) {
          toast({
            variant: 'destructive',
            title: t('File too large', 'Filen är för stor'),
            description: t(
              `${file.name} exceeds ${MAX_FILE_SIZE_MB}MB limit`,
              `${file.name} överskrider ${MAX_FILE_SIZE_MB}MB gränsen`
            ),
          })
          continue
        }
        valid.push(file)
      }
      if (valid.length) {
        setAttachments((prev) => [...prev, ...valid])
      }
    },
    [toast, t]
  )

  useEffect(() => {
    handlePasteRef.current = (event: ClipboardEvent) => {
      if (!editor || editor.isDestroyed || disabled || isPending) return false
      const clipboard = event.clipboardData
      if (!clipboard) return false

      const imageFiles = clipboardImageFiles(clipboard)
      if (imageFiles.length) {
        event.preventDefault()
        editor.chain().focus().deleteSelection().run()
        addAttachmentFiles(imageFiles)
        return true
      }

      const { files, remoteUrls, leftover } = parsePastedImageSources(
        clipboard.getData('text/plain'),
        clipboard.getData('text/html')
      )
      if (!files.length && !remoteUrls.length) return false

      event.preventDefault()
      editor.chain().focus().deleteSelection().run()
      if (leftover) {
        editor.view.dispatch(editor.view.state.tr.insertText(leftover))
      }
      if (files.length) addAttachmentFiles(files)
      if (!remoteUrls.length) return true

      void Promise.all(
        remoteUrls.map((url, index) =>
          fileFromImageUrl(url, files.length + index)
        )
      ).then((fetched) => {
        const loaded = fetched.filter((file): file is File => file != null)
        if (loaded.length) addAttachmentFiles(loaded)
        const failed = remoteUrls.filter((_, index) => fetched[index] == null)
        if (!failed.length) return
        if (!editor.isDestroyed) {
          editor.commands.focus()
          editor.view.dispatch(
            editor.view.state.tr.insertText(failed.join(' '))
          )
        }
        toast({
          variant: 'destructive',
          title: t('Could not load image', 'Kunde inte ladda bilden'),
          description: t(
            'Paste the image itself, or attach the file.',
            'Klistra in bilden eller bifoga filen.'
          ),
        })
      })

      return true
    }
  }, [addAttachmentFiles, disabled, editor, isPending, t, toast])

  const handleFileSelect = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(e.target.files ?? [])
      e.target.value = ''
      addAttachmentFiles(files)
    },
    [addAttachmentFiles]
  )

  const removeAttachment = useCallback((index: number) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index))
  }, [])

  const handleSubmit = useCallback(async () => {
    if (!editor || disabled || isPending) return
    const content = editor.getHTML()
    const rawText = editor.getText()
    const text = rawText.trim()
    const hasContent = !!text && !isSaraPrefillOnly(rawText)
    const hasAttachments = attachments.length > 0

    if (!hasContent && !hasAttachments) return

    if (hasAttachments && !storageFolderId) {
      toast({
        variant: 'destructive',
        title: t('Failed', 'Misslyckades'),
        description: t(
          'Unable to upload attachments right now. Try again in a moment.',
          'Kunde inte ladda upp bilagor just nu. Försök igen om en stund.'
        ),
      })
      return
    }

    try {
      let attachmentUrls: string[] = []
      if (hasAttachments && storageFolderId) {
        for (const file of attachments) {
          const sanitizedName = sanitizeFilename(file.name)
          const fileName = `${Date.now()}-${sanitizedName}`
          const filePath = `${storageFolderId}/${fileName}`

          const { error: uploadError } = await supabase.storage
            .from(uploadBucket)
            .upload(filePath, file)

          if (uploadError) throw uploadError

          const {
            data: { publicUrl },
          } = supabase.storage.from(uploadBucket).getPublicUrl(filePath)
          attachmentUrls.push(publicUrl)
        }
      }

      editor.commands.clearContent()
      setEditorPlainText('')
      setIsEmpty(true)
      requestAnimationFrame(syncEditorHeight)
      setAttachments([])

      await onSubmit(
        hasContent ? content : '',
        attachmentUrls,
        hasContent ? text : ''
      )
    } catch (err) {
      console.error('Chat upload error:', err)

      toast({
        variant: 'destructive',
        title: t('Failed', 'Misslyckades'),
        description: err instanceof Error ? err.message : undefined,
      })
    }
  }, [
    editor,
    disabled,
    isPending,
    attachments,
    storageFolderId,
    uploadBucket,
    supabase,
    onSubmit,
    toast,
    t,
    syncEditorHeight,
  ])

  useEffect(() => {
    handleSubmitRef.current = handleSubmit
  }, [handleSubmit])

  useEffect(() => {
    if (!editor) return
    setIsEmpty(editor.isEmpty)
    setEditorPlainText(editor.getText())
  }, [editor])

  if (!editor) return null

  const canSend =
    (!isEmpty && !isSaraPrefillOnly(editorPlainText)) || attachments.length > 0
  const isDisabled = disabled || isPending
  const showStop = Boolean(isGenerating && onStop && !canSend)

  return (
    <form
      className={cn(embedded ? 'px-3 pb-3 pt-2' : 'px-5 pb-5', className)}
      onSubmit={(e) => {
        e.preventDefault()
        handleSubmit()
      }}
      onDragOver={(e) => {
        e.preventDefault()
        if (!isDisabled) setIsDragOver(true)
      }}
      onDragLeave={() => setIsDragOver(false)}
      onDrop={(e) => {
        e.preventDefault()
        setIsDragOver(false)
        if (isDisabled) return
        const files = Array.from(e.dataTransfer.files ?? []).filter((file) =>
          file.type.startsWith('image/')
        )
        addAttachmentFiles(files)
      }}
    >
      <Column
        className={cn(
          'group relative overflow-hidden rounded-2xl [background:var(--raised-gradient)] border border-[var(--raised-border)] shadow-[var(--shadow-lift)] transition-shadow focus-within:border-[var(--raised-border-hover)]',
          isDragOver && 'border-violet-300 ring-2 ring-violet-200'
        )}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept="image/*,.pdf"
          className="hidden"
          onChange={handleFileSelect}
        />

        {attachments.length > 0 && (
          <Row className="flex-wrap gap-2 border-b border-gray-100 px-4 py-2.5">
            {attachments.map((file, index) => (
              <AttachmentChip
                key={`${file.name}-${file.size}-${index}`}
                file={file}
                onRemove={() => removeAttachment(index)}
                removeLabel={t('Remove', 'Ta bort')}
              />
            ))}
          </Row>
        )}

        <div
          ref={editorAreaRef}
          className="relative px-4 pb-11 pt-3.5 text-base [&_.sara-mention-highlight]:inline-block [&_.sara-mention-highlight]:rounded-md [&_.sara-mention-highlight]:bg-violet-50 [&_.sara-mention-highlight]:px-1.5 [&_.sara-mention-highlight]:py-1 [&_.sara-mention-highlight]:mr-1.5 [&_.sara-mention-highlight]:align-middle [&_.sara-mention-highlight]:font-semibold [&_.sara-mention-highlight]:leading-none [&_.sara-mention-highlight]:text-violet-700 [&_.sara-mention-highlight]:shadow-[inset_0_0_0_1px_rgba(124,58,237,0.15)] [&_.sara-mention-highlight]:[box-decoration-break:clone] [&_.tiptap]:box-border [&_.tiptap]:min-h-0 [&_.tiptap]:p-0 [&_.tiptap]:text-base [&_.tiptap]:outline-none [&_.tiptap_p.is-editor-empty:first-child::before]:text-muted-foreground/60 [&_.tiptap_p.is-editor-empty:first-child::before]:content-[attr(data-placeholder)] [&_.tiptap_p.is-editor-empty:first-child::before]:float-left [&_.tiptap_p.is-editor-empty:first-child::before]:pointer-events-none [&_.tiptap_p.is-editor-empty:first-child::before]:h-0"
        >
          {showSaraTabHint ? (
            <SaraTabHintPlaceholder
              toWriteTo={t('to ask', 'för att fråga')}
              actionLabel={tabSuggestion?.hintLabel}
            />
          ) : null}
          {showTabSuggestionGhost &&
          tabSuggestion &&
          suggestionOffset.left > 0 ? (
            <button
              type="button"
              className="absolute z-[2] max-w-[calc(100%-0.5rem)] cursor-pointer overflow-hidden text-ellipsis whitespace-nowrap text-left text-[15px] leading-[1.46667]"
              style={{
                left: suggestionOffset.left,
                top: suggestionOffset.top,
                maxWidth: `calc(100% - ${suggestionOffset.left}px - 8px)`,
              }}
              onMouseDown={(event) => event.preventDefault()}
              onClick={acceptTabSuggestion}
              aria-label={t(
                `Use suggestion: ${tabSuggestion.insertText}`,
                `Använd förslag: ${tabSuggestion.insertText}`
              )}
            >
              <AnimatedSuggestionPlaceholder text={tabSuggestion.insertText} />
            </button>
          ) : null}
          <EditorContent editor={editor} />
        </div>

        <Row className="absolute inset-x-2 bottom-2 items-end justify-between gap-2">
          <Row className="min-w-0 flex-wrap items-center gap-0 opacity-60 transition-opacity duration-150 group-focus-within:opacity-100">
            <FormatButton
              onClick={() => fileInputRef.current?.click()}
              disabled={isDisabled}
              ariaLabel={t('Attach file', 'Bifoga fil')}
            >
              <Paperclip className="h-4 w-4" strokeWidth={2} />
            </FormatButton>
            <div className="hidden items-center md:flex">
              <ToolbarDivider />
              <FormatButton
                onClick={() => editor.chain().focus().toggleBold().run()}
                isActive={editor.isActive('bold')}
                disabled={isDisabled}
                ariaLabel={t('Bold', 'Fet')}
              >
                <Bold className="h-3.5 w-3.5" strokeWidth={2} />
              </FormatButton>
              <FormatButton
                onClick={() => editor.chain().focus().toggleItalic().run()}
                isActive={editor.isActive('italic')}
                disabled={isDisabled}
                ariaLabel={t('Italic', 'Kursiv')}
              >
                <Italic className="h-3.5 w-3.5" strokeWidth={2} />
              </FormatButton>
              <FormatButton
                onClick={() => editor.chain().focus().toggleUnderline().run()}
                isActive={editor.isActive('underline')}
                disabled={isDisabled}
                ariaLabel={t('Underline', 'Understruken')}
              >
                <Underline className="h-3.5 w-3.5" strokeWidth={2} />
              </FormatButton>
              <FormatButton
                onClick={() => editor.chain().focus().toggleStrike().run()}
                isActive={editor.isActive('strike')}
                disabled={isDisabled}
                ariaLabel={t('Strikethrough', 'Genomstruken')}
              >
                <Strikethrough className="h-3.5 w-3.5" strokeWidth={2} />
              </FormatButton>
              <ToolbarDivider />
              <FormatButton
                onClick={() => {
                  if (editor.isActive('link')) {
                    editor.chain().focus().unsetLink().run()
                  } else {
                    const url = window.prompt(t('Enter URL', 'Ange URL'))
                    if (url) editor.chain().focus().setLink({ href: url }).run()
                  }
                }}
                isActive={editor.isActive('link')}
                disabled={isDisabled}
                ariaLabel={t('Link', 'Länk')}
              >
                <Link2 className="h-3.5 w-3.5" strokeWidth={2} />
              </FormatButton>
              <FormatButton
                onClick={() => editor.chain().focus().toggleOrderedList().run()}
                isActive={editor.isActive('orderedList')}
                disabled={isDisabled}
                ariaLabel={t('Numbered list', 'Numrerad lista')}
              >
                <ListOrdered className="h-3.5 w-3.5" strokeWidth={2} />
              </FormatButton>
              <FormatButton
                onClick={() => editor.chain().focus().toggleBulletList().run()}
                isActive={editor.isActive('bulletList')}
                disabled={isDisabled}
                ariaLabel={t('Bullet list', 'Punktlista')}
              >
                <List className="h-3.5 w-3.5" strokeWidth={2} />
              </FormatButton>
            </div>
          </Row>

          {showStop ? (
            <button
              type="button"
              onClick={onStop}
              aria-label={t('Stop', 'Stoppa')}
              className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-gray-900 text-white shadow-sm hover:bg-gray-800"
            >
              <Square className="h-3 w-3" fill="currentColor" strokeWidth={0} />
            </button>
          ) : (
            <button
              type="submit"
              disabled={!canSend || isDisabled}
              aria-label={
                noteMode
                  ? t('Add to chat', 'Lägg till i chatten')
                  : t('Send', 'Skicka')
              }
              className={cn(
                'flex h-8 w-8 shrink-0 items-center justify-center rounded-lg transition-all',
                canSend && !isDisabled
                  ? 'bg-gray-900 text-white shadow-sm hover:bg-gray-800'
                  : 'cursor-not-allowed bg-gray-100 text-gray-400'
              )}
            >
              <ArrowUp className="h-4 w-4" strokeWidth={2.5} />
            </button>
          )}
        </Row>
      </Column>
    </form>
  )
}
